import type { ColumnFilters, ColumnFilterValue } from '@/models/flowFilter.type';
import {
  applyFiltersToSearchParams,
  searchParamsToFilters,
  type ColumnFilterKinds,
} from '@/shared/utils/column-filters-url';
import { buildFlowFilter, type RelationColumns } from '@/shared/utils/flow-filter-utils';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const NO_DEFAULT_FILTERS: ColumnFilters = {};

type UseColumnFiltersOptions = {
  /** Appelé à chaque modification des filtres (ex. pour revenir à la première page). */
  onChange?: () => void;
  /** Colonnes de relation filtrées sur un champ de l'objet lié (voir `buildFlowFilter`). */
  relationColumns?: RelationColumns;
  /**
   * Type de filtre par colonne. Si fourni, les filtres vivent dans les paramètres d'URL
   * (une clé par colonne) au lieu de l'état local, ce qui permet de lier une vue filtrée.
   */
  urlKinds?: ColumnFilterKinds;
  /** Paramètres d'URL supprimés à chaque modification des filtres (ex. `page`). */
  resetParams?: string[];
  /**
   * Filtres appliqués tant que l'utilisateur ne les a pas modifiés (référence stable attendue).
   * En mode URL, une colonne vidée garde un paramètre vide pour ne pas revenir à son défaut ;
   * `resetAll` rétablit les défauts.
   */
  defaultFilters?: ColumnFilters;
};

type UpdateFiltersOptions = {
  /** Colonne vidée par l'utilisateur : son défaut ne doit pas revenir. */
  cleared?: string;
  /** Réinitialisation : les colonnes vidées retrouvent leur défaut. */
  reset?: boolean;
};

/**
 * État des filtres par colonne d'un tableau et filtre JSON (`FlowFilter`) à envoyer au flow.
 * Une valeur vide (texte vide, liste vide, plage sans borne) retire le filtre de la colonne.
 */
const useColumnFilters = ({
  onChange,
  relationColumns,
  urlKinds,
  resetParams = [],
  defaultFilters = NO_DEFAULT_FILTERS,
}: UseColumnFiltersOptions = {}) => {
  const [localFilters, setLocalFilters] = useState<ColumnFilters>(defaultFilters);
  const [searchParams, setSearchParams] = useSearchParams();

  const searchKey = searchParams.toString();
  const urlFilters = useMemo(() => {
    if (!urlKinds) return undefined;
    const params = new URLSearchParams(searchKey);
    // Une colonne absente de l'URL prend son défaut ; présente mais vide, elle n'est pas filtrée.
    const defaults = Object.fromEntries(
      Object.entries(defaultFilters).filter(([accessor]) => !params.has(accessor)),
    );
    return { ...defaults, ...searchParamsToFilters(params, urlKinds) };
  }, [searchKey, urlKinds, defaultFilters]);
  const filters = urlFilters ?? localFilters;
  // `resetParams` est un littéral recréé à chaque rendu : sa valeur sérialisée sert de dépendance.
  const resetKey = resetParams.join(',');

  const updateFilters = useCallback(
    (
      update: (previous: ColumnFilters) => ColumnFilters,
      { cleared, reset = false }: UpdateFiltersOptions = {},
    ) => {
      if (!urlKinds) {
        setLocalFilters(update);
        return;
      }
      setSearchParams(
        previous => {
          const previousFilters = searchParamsToFilters(previous, urlKinds);
          const nextFilters = update(previousFilters);
          const next = applyFiltersToSearchParams(previous, nextFilters, urlKinds);
          if (!reset) {
            Object.keys(defaultFilters).forEach(accessor => {
              const wasCleared = previous.has(accessor) && !(accessor in previousFilters);
              if (!(accessor in nextFilters) && (wasCleared || accessor === cleared)) {
                next.set(accessor, '');
              }
            });
          }
          resetKey.split(',').forEach(param => next.delete(param));
          return next;
        },
        { replace: true },
      );
    },
    [urlKinds, setSearchParams, resetKey, defaultFilters],
  );

  const setFilter = useCallback(
    (accessor: string, value: ColumnFilterValue | undefined) => {
      const isActive = value != null && buildFlowFilter({ [accessor]: value }) != null;
      updateFilters(
        previous => (isActive ? { ...previous, [accessor]: value } : omit(previous, accessor)),
        { cleared: isActive ? undefined : accessor },
      );
      onChange?.();
    },
    [updateFilters, onChange],
  );

  const resetAll = useCallback(() => {
    updateFilters(() => (urlKinds ? {} : defaultFilters), { reset: true });
    onChange?.();
  }, [updateFilters, onChange, urlKinds, defaultFilters]);

  const isFiltering = useCallback((accessor: string) => accessor in filters, [filters]);

  const flowFilter = useMemo(
    () => buildFlowFilter(filters, relationColumns),
    [filters, relationColumns],
  );

  // Filtres modifiés par rapport à l'état initial (défauts compris).
  const hasFilters = urlKinds
    ? Object.keys(urlKinds).some(accessor => searchParams.has(accessor))
    : !isEqual(localFilters, defaultFilters);

  return {
    filters,
    setFilter,
    resetAll,
    isFiltering,
    hasFilters,
    flowFilter,
  };
};

export default useColumnFilters;
